import { CityWithDistrict } from 'graphql/types'
import { Grid, TextField, Typography } from '@material-ui/core'
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AvailabilityCheckField } from 'store/AvailabilityCheck/AvailabilityCheck.actions'
import { ViewType } from 'store/GeneralState/GeneralState.reducer'
import TestIDs from 'utils/TestIDs'
import Dropdown from '../Dropdown'
import './customAnchor.css'

function getCityDisplayLabel(city: CityWithDistrict): string {
    return city.district ? `${city.city} - ${city.district}` : city.city
}

export interface AvailabilityCheckInputProps {
    noProductLink: string
    autoFocusOnMount: boolean
    enableAddressAddition?: boolean
    cities: CityWithDistrict[]
    disabledCity: boolean
    disabledHouseNumber: boolean
    disabledStreet: boolean
    disabledZip: boolean
    disabledAddition: boolean
    focusedFieldId: AvailabilityCheckField
    houseNumbers: string[]
    selectedCity: string
    selectedDistrict: string
    selectedHouseNumber: string
    selectedStreet: string
    selectedAddition: string
    streets: string[]
    additions: string[]
    zip: string
    queriesAreFinished?: boolean
    setFocusedField: (payload: AvailabilityCheckField) => void
    setSelectedCityAndDistrict?: (
        payload: React.ChangeEvent<{
            name?: string | undefined
            value: unknown
        }>,
    ) => void
    setSelectedHouseNumber?: (
        payload: React.ChangeEvent<{
            name?: string | undefined
            value: unknown
        }>,
    ) => void
    setSelectedStreet?: (
        payload: React.ChangeEvent<{
            name?: string | undefined
            value: unknown
        }>,
    ) => void
    setSelectedAddition?: (
        payload: React.ChangeEvent<{
            name?: string | undefined
            value: unknown
        }>,
    ) => void
    setZip?: (payload: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    haveLink?: boolean
}

const AvailabilityCheckInput = (props: AvailabilityCheckInputProps): JSX.Element => {
    const { t } = useTranslation()
    const regexp = /^[0-9\b]+$/
    const {
        enableAddressAddition,
        autoFocusOnMount,
        cities,
        disabledCity,
        disabledHouseNumber,
        disabledStreet,
        disabledZip,
        disabledAddition,
        focusedFieldId,
        houseNumbers,
        selectedCity,
        selectedDistrict,
        selectedHouseNumber,
        selectedStreet,
        selectedAddition,
        streets,
        zip,
        additions,
        queriesAreFinished,
        setFocusedField,
        setSelectedCityAndDistrict,
        setSelectedHouseNumber,
        setSelectedStreet,
        setZip,
        setSelectedAddition,
        noProductLink,
        haveLink,
    } = props

    const cityOptions = useMemo(() => cities.map(getCityDisplayLabel), [cities])
    const selectedCityValue = useMemo(
        () => (selectedCity ? (selectedDistrict ? `${selectedCity} - ${selectedDistrict}` : selectedCity) : ''),
        [selectedCity, selectedDistrict],
    )

    const zipRef = useRef<HTMLInputElement>(null)
    const cityRef = useRef<HTMLInputElement>(null)
    const streetRef = useRef<HTMLInputElement>(null)
    const houseNumberRef = useRef<HTMLInputElement>(null)
    const additionRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        setTimeout(() => {
            switch (focusedFieldId) {
                case AvailabilityCheckField.ZIP:
                    if (zipRef.current !== null) {
                        zipRef.current.focus()
                    }
                    break
                case AvailabilityCheckField.CITY:
                    if (cityRef.current !== null) {
                        cityRef.current.focus()
                    }
                    break
                case AvailabilityCheckField.STREET:
                    if (streetRef.current !== null) {
                        streetRef.current.focus()
                    }
                    break
                case AvailabilityCheckField.HOUSE_NUMBER:
                    if (houseNumberRef.current !== null) {
                        houseNumberRef.current.focus()
                    }
                    break
                case AvailabilityCheckField.ADDITION:
                    if (additionRef.current !== null) {
                        additionRef.current.focus()
                    }
                    break
            }
        }, 10)
    }, [focusedFieldId, zip, selectedCity, selectedHouseNumber, selectedStreet, selectedAddition, selectedDistrict])

    const nothingFound = queriesAreFinished === true && cities.length === 0 && zip.length === 5

    return (
        <>
            <Grid container {...TestIDs.GET_AVAILABILITY_CHECK('FORM')} spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        {...TestIDs.GET_AVAILABILITY_CHECK('ZIP_INPUT')}
                        error={nothingFound}
                        disabled={disabledZip}
                        InputProps={{
                            className: nothingFound ? 'TextError' : '',
                            onFocus: (): void => setFocusedField(AvailabilityCheckField.ZIP),
                        }}
                        inputProps={{
                            maxLength: 5,
                        }}
                        inputRef={zipRef}
                        fullWidth={true}
                        label={t('zip')}
                        variant={'outlined'}
                        value={zip}
                        onChange={(e): void => {
                            if (
                                setZip &&
                                ((e.currentTarget.value !== '' && regexp.test(e.currentTarget.value)) ||
                                    e.currentTarget.value === '')
                            ) {
                                setZip(e)
                            }
                        }}
                        autoFocus={autoFocusOnMount}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={8}>
                    {cities.length === 1 ? (
                        <TextField
                            {...TestIDs.GET_AVAILABILITY_CHECK('CITY_INPUT')}
                            disabled={true}
                            fullWidth={true}
                            label={t('city')}
                            variant={'outlined'}
                            value={getCityDisplayLabel(cities[0])}
                        />
                    ) : (
                        <Dropdown
                            testIdentifier={{
                                input: 'CITY_SELECT',
                                viewType: ViewType.AVAILABILITY_CHECK,
                            }}
                            label={t('city')}
                            disabled={disabledCity}
                            inputRef={cityRef}
                            value={selectedCityValue}
                            onChange={setSelectedCityAndDistrict}
                            options={cityOptions}
                        />
                    )}
                </Grid>
                <Grid item xs={12} md={8}>
                    {streets.length === 1 ? (
                        <TextField
                            {...TestIDs.GET_AVAILABILITY_CHECK('STREET_INPUT')}
                            disabled={true}
                            fullWidth={true}
                            label={t('street')}
                            variant={'outlined'}
                            value={streets[0]}
                        />
                    ) : (
                        <Dropdown
                            testIdentifier={{
                                input: 'STREET_SELECT',
                                viewType: ViewType.AVAILABILITY_CHECK,
                            }}
                            label={t('street')}
                            disabled={disabledStreet}
                            inputRef={streetRef}
                            value={selectedStreet}
                            onChange={setSelectedStreet}
                            options={streets}
                        />
                    )}
                </Grid>
                <Grid item xs={12} md={enableAddressAddition ? 2 : 4}>
                    {houseNumbers.length === 1 ? (
                        <TextField
                            {...TestIDs.GET_AVAILABILITY_CHECK('HOUSE_NUMBER_INPUT')}
                            disabled={true}
                            fullWidth={true}
                            label={t('number')}
                            variant={'outlined'}
                            value={houseNumbers[0]}
                        />
                    ) : (
                        <Dropdown
                            label={t('number')}
                            testIdentifier={{
                                input: 'HOUSE_NUMBER_SELECT',
                                viewType: ViewType.AVAILABILITY_CHECK,
                            }}
                            inputRef={houseNumberRef}
                            disabled={disabledHouseNumber}
                            value={selectedHouseNumber}
                            onChange={setSelectedHouseNumber}
                            options={houseNumbers}
                        />
                    )}
                </Grid>
                {enableAddressAddition && (
                    <Grid item xs={12} md={2}>
                        {additions.length === 1 ? (
                            <TextField
                                {...TestIDs.GET_AVAILABILITY_CHECK('HOUSE_NUMBER_INPUT')}
                                disabled={true}
                                fullWidth={true}
                                label={t('addition')}
                                variant={'outlined'}
                                value={additions[0]}
                            />
                        ) : (
                            <Dropdown
                                label={t('addition')}
                                testIdentifier={{
                                    input: 'HOUSE_NUMBER_SELECT',
                                    viewType: ViewType.AVAILABILITY_CHECK,
                                }}
                                inputRef={additionRef}
                                disabled={disabledAddition}
                                value={selectedAddition}
                                displayEmpty={true}
                                onChange={setSelectedAddition}
                                options={additions}
                            />
                        )}
                    </Grid>
                )}
                {haveLink === true && (
                    <Grid item xs={12}>
                        <Typography
                            variant={'body1'}
                            dangerouslySetInnerHTML={{
                                __html: t('notYetAvailableLink', {
                                    link: noProductLink,
                                }),
                            }}
                        />
                    </Grid>
                )}
            </Grid>
        </>
    )
}

export default AvailabilityCheckInput
